// src/components/ui/Pagination.tsx
import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Button from './Button';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    className?: string;
    showPageInfo?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    className = '',
    showPageInfo = true
}) => {
    const { t } = useTranslation();

    if (totalPages <= 1) return null;

    const getVisiblePages = () => {
        const visiblePages: (number | string)[] = [];
        const maxVisiblePages = 7;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages is small
            for (let i = 1; i <= totalPages; i++) {
                visiblePages.push(i);
            }
        } else {
            // Show pages with ellipsis
            if (currentPage <= 3) {
                // Show first 5 pages + ellipsis + last page
                for (let i = 1; i <= 5; i++) {
                    visiblePages.push(i);
                }
                if (totalPages > 6) {
                    visiblePages.push('...');
                }
                visiblePages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // Show first page + ellipsis + last 5 pages
                visiblePages.push(1);
                if (totalPages > 6) {
                    visiblePages.push('...');
                }
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    visiblePages.push(i);
                }
            } else {
                // Show first + ellipsis + current-1, current, current+1 + ellipsis + last
                visiblePages.push(1);
                visiblePages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    visiblePages.push(i);
                }
                visiblePages.push('...');
                visiblePages.push(totalPages);
            }
        }

        return visiblePages;
    };

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className} `}>
            {showPageInfo && (
                <div className="text-sm text-gray-700 dark:text-gray-300">
                    {t('common.pagination.showing', {
                        start: startItem,
                        end: endItem,
                        total: totalItems
                    })}
                </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-2 flex-wrap order-2 sm:order-none w-full sm:w-auto">
                {/* Previous Button */}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    leftIcon={<FiChevronLeft />}
                    className="px-3 py-2 order-3 sm:order-none w-full sm:w-auto sm:flex-1"
                >
                    {t('common.pagination.previous')}
                </Button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1 order-1 sm:order-none w-full sm:w-auto justify-center sm:justify-start">
                    {getVisiblePages().map((page, index) => {
                        if (page === '...') {
                            return (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="px-3 py-2 text-gray-500 dark:text-gray-400"
                                >
                                    ...
                                </span>
                            );
                        }

                        const pageNumber = page as number;
                        const isActive = pageNumber === currentPage;

                        return (
                            <button
                                key={pageNumber}
                                onClick={() => onPageChange(pageNumber)}
                                className={`
                                    px-3 py-2 text-sm font-medium rounded-md transition-colors
                                    ${isActive
                                        ? 'bg-primary text-white'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }
                                `}
                            >
                                {pageNumber}
                            </button>
                        );
                    })}
                </div>

                {/* Next Button */}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    rightIcon={<FiChevronRight />}
                    className="px-3 py-2 order-4 sm:order-none w-full sm:w-auto sm:flex-1"
                >
                    {t('common.pagination.next')}
                </Button>
            </div>
        </div>
    );
};

export default Pagination;